import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, ArrowLeftRight, Scale } from "lucide-react";
import { API } from "@/lib/api";

const TYPES = { bank: "Banka", cash: "Nakit", credit_card: "Kredi Kartı", dbs: "DBS (Doğrudan Borçlandırma)" };
const COLORS = ["#0A0A0A", "#059669", "#E11D48", "#F59E0B", "#2563EB", "#7C3AED", "#78716C"];

export default function Accounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [balances, setBalances] = useState({});
  const [availables, setAvailables] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/accounts");
    setItems(data);
    const { data: dash } = await api.get("/dashboard/summary");
    const bmap = {}, amap = {};
    dash.accounts.forEach((a) => {
      bmap[a.id] = a.balance;
      amap[a.id] = a.available_balance ?? a.balance;
    });
    setBalances(bmap);
    setAvailables(amap);
  }, []);
  useEffect(() => { load(); }, [load]);

  const zeroBalance = async (a) => {
    const bal = balances[a.id] ?? a.opening_balance ?? 0;
    if (Math.abs(bal) < 0.005) {
      toast.info("Bakiye zaten sıfır");
      return;
    }
    const isPositive = bal > 0;
    const kind = isPositive ? "gider (bakiye düşer)" : "gelir (bakiye yükselir)";
    const note = window.prompt(
      `'${a.name}' hesabının mevcut bakiyesi: ${fmtTRY(bal)}\n\n` +
      `Denkleştirme için ${fmtTRY(Math.abs(bal))} tutarında ${kind} kaydı oluşturulacak.\n` +
      `Bu kayıt herhangi bir cariyi ETKİLEMEZ — sadece bu hesabın bakiyesini sıfırlar.\n\n` +
      `Açıklama (opsiyonel):`,
      "Bakiye Sıfırlama",
    );
    if (note === null) return; // Cancel
    try {
      const { data } = await api.post(`/accounts/${a.id}/zero-balance`, {
        description: note || "Bakiye Sıfırlama",
      });
      toast.success(`Bakiye sıfırlandı. Önceki: ${fmtTRY(data.previous_balance)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sıfırlanamadı");
    }
  };

  const remove = async (id) => {
    // Silme öncesi bağlı kayıt sayısını göster.
    let refs = null;
    try {
      const { data } = await api.get(`/accounts/${id}/refs`);
      refs = data;
    } catch { /* ignore, silmeye zorla */ }
    if (refs && refs.total > 0) {
      const parts = [
        refs.transactions && `${refs.transactions} işlem`,
        refs.cheques && `${refs.cheques} çek`,
        refs.loans && `${refs.loans} kredi`,
        refs.card_statements && `${refs.card_statements} kart ekstresi`,
        refs.upcoming && `${refs.upcoming} yaklaşan`,
        refs.todos && `${refs.todos} yapılacak`,
        refs.guarantees && `${refs.guarantees} teminat`,
      ].filter(Boolean).join(", ");
      toast.error(
        `Bu hesap silinemez: ${parts} bağlı. Önce bunları taşıyın veya silin.`,
        { duration: 7000 },
      );
      return;
    }
    if (!window.confirm("Hesap silinsin mi?")) return;
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Silindi"); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Silinemedi");
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Bankalar & Kasa</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Hesaplar</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="transfer-open-button"
            onClick={() => setTransferOpen(true)}
            disabled={items.length < 2}
            className="brutal-btn bg-white border border-black px-4 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title={items.length < 2 ? "Virman için en az 2 hesap gerekli" : "Hesaplar arası virman"}
          >
            <ArrowLeftRight size={16} /> Virman
          </button>
          <button data-testid="account-add-button" onClick={() => { setEditing(null); setOpen(true); }}
                  className="brutal-btn bg-black text-white px-4 py-2.5 text-sm font-medium flex items-center gap-2">
            <Plus size={16} /> Yeni Hesap
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="brutal-card p-8 text-center col-span-full text-neutral-500 text-sm">
            Henüz hesap eklenmedi.
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="brutal-card p-6" data-testid={`account-card-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-12" style={{ background: a.color }} />
                <div>
                  <div className="font-display text-lg font-bold">{a.name}</div>
                  <div className="label-caps mt-1">{TYPES[a.account_type]} {a.bank_name && `· ${a.bank_name}`}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={`account-zero-btn-${a.id}`}
                  onClick={() => zeroBalance(a)}
                  className="p-1.5 hover:bg-amber-50"
                  title="Bakiyeyi denkleştir (sıfırla) — cari kayıtları etkilenmez"
                >
                  <Scale size={14} className="text-amber-700" />
                </button>
                <button onClick={() => { setEditing(a); setOpen(true); }} className="p-1.5 hover:bg-neutral-100"><Edit3 size={14} /></button>
                <button onClick={() => remove(a.id)} className="p-1.5 hover:bg-rose-50"><Trash2 size={14} className="text-rose-600" /></button>
              </div>
            </div>
            {a.iban && <div className="mt-4 font-mono-num text-xs text-neutral-500">{a.iban}</div>}
            <div className="mt-6 pt-4 border-t border-black/10">
              <div className="label-caps">Bakiye</div>
              <div className={`font-mono-num text-2xl font-semibold mt-1 ${(balances[a.id] ?? 0) < 0 ? "text-rose-600" : ""}`}>
                {fmtTRY(balances[a.id] ?? a.opening_balance)}
              </div>
              {a.has_overdraft && (
                <div className="mt-3 pt-3 border-t border-dashed border-black/10">
                  <div className="flex items-center justify-between text-xs">
                    <span className="label-caps">KMH Limiti</span>
                    <span className="font-mono-num text-neutral-500">{fmtTRY(a.overdraft_limit || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="label-caps">Kullanılabilir</span>
                    <span
                      data-testid={`account-available-${a.id}`}
                      className={`font-mono-num text-lg font-semibold ${(availables[a.id] ?? 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}
                    >
                      {fmtTRY(availables[a.id] ?? (balances[a.id] ?? 0) + (a.overdraft_limit || 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <AccountModal editing={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      )}
      {transferOpen && (
        <TransferModal
          accounts={items}
          balances={balances}
          onClose={() => setTransferOpen(false)}
          onSaved={() => { setTransferOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function AccountModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    name: "", bank_name: "", account_type: "bank",
    iban: "", opening_balance: 0, color: COLORS[0],
    has_overdraft: false, overdraft_limit: 0,
  });
  const save = async (e) => {
    e.preventDefault();
    const p = {
      ...form,
      opening_balance: Number(form.opening_balance),
      has_overdraft: !!form.has_overdraft,
      overdraft_limit: form.has_overdraft ? Number(form.overdraft_limit) || 0 : 0,
    };
    try {
      if (editing) await api.put(`/accounts/${editing.id}`, p);
      else await api.post("/accounts", p);
      toast.success("Kaydedildi"); onSaved();
    } catch { toast.error("Hata"); }
  };
  const isBank = form.account_type === "bank";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-black w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Hesabı Düzenle" : "Yeni Hesap"}</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Ad">
            <input required data-testid="account-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tür">
              <select data-testid="account-type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="input bg-white">
                {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Banka">
              <input data-testid="account-bank" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="input" />
            </Field>
          </div>
          <Field label="IBAN">
            <input data-testid="account-iban" value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="input font-mono-num" placeholder="TR__ ____ ____ ____ ____ __" />
          </Field>
          <Field label="Açılış Bakiyesi (₺)">
            <input required type="number" step="0.01" data-testid="account-balance" value={form.opening_balance}
                   onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="input font-mono-num" />
          </Field>
          {isBank && (
            <div className="border border-black/10 p-3 bg-neutral-50 space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="account-has-overdraft"
                  checked={!!form.has_overdraft}
                  onChange={(e) => setForm({ ...form, has_overdraft: e.target.checked })}
                  className="w-4 h-4 accent-black"
                />
                <span className="font-semibold">KMH Hesabı</span>
                <span className="text-xs text-neutral-500">(Kredili Mevduat — ana hesapla ortak)</span>
              </label>
              {form.has_overdraft && (
                <Field label="KMH Limiti (₺)">
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="account-overdraft-limit"
                    value={form.overdraft_limit}
                    onChange={(e) => setForm({ ...form, overdraft_limit: e.target.value })}
                    className="input font-mono-num"
                    placeholder="Örn: 50000"
                  />
                </Field>
              )}
              {form.has_overdraft && (
                <p className="text-[11px] text-neutral-500">
                  Kullanılabilir bakiye = Mevcut Bakiye + KMH Limiti. Hesap eksiye düşerse KMH kullanımı başlar.
                </p>
              )}
            </div>
          )}
          <Field label="Renk">
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                        className={`w-8 h-8 border-2 ${form.color === c ? "border-black" : "border-transparent"}`} style={{ background: c }} />
              ))}
            </div>
          </Field>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="account-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
      </form>
      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}

function TransferModal({ accounts, balances, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    from_id: accounts[0]?.id || "",
    to_id: (accounts.find((a) => a.id !== accounts[0]?.id) || {}).id || "",
    amount: "",
    date: today,
    description: "",
  });

  const fromAcc = accounts.find((a) => a.id === form.from_id);
  const toAcc = accounts.find((a) => a.id === form.to_id);
  const fromBalance = balances[form.from_id] ?? fromAcc?.opening_balance ?? 0;
  const amountNum = Number(form.amount) || 0;
  const willOverdraw = amountNum > fromBalance && fromAcc?.account_type !== "credit_card";

  const save = async (e) => {
    e.preventDefault();
    if (!form.from_id || !form.to_id) { toast.error("Kaynak ve hedef hesap seçin"); return; }
    if (form.from_id === form.to_id) { toast.error("Kaynak ve hedef hesap aynı olamaz"); return; }
    if (!amountNum || amountNum <= 0) { toast.error("Geçerli tutar girin"); return; }
    try {
      await api.post("/transactions", {
        type: "transfer",
        amount: amountNum,
        date: form.date,
        account_id: form.from_id,
        to_account_id: form.to_id,
        category: "Virman",
        description: form.description || `Virman: ${fromAcc?.name} → ${toAcc?.name}`,
      });
      toast.success("Virman kaydedildi");
      onSaved();
    } catch { toast.error("Virman kaydedilemedi"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="transfer-form-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-black w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="label-caps">Hesaplar Arası</div>
            <h2 className="font-display text-xl font-bold">Virman</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <Field label="Kaynak Hesap">
              <select
                required data-testid="transfer-from"
                value={form.from_id}
                onChange={(e) => setForm({ ...form, from_id: e.target.value })}
                className="input bg-white"
              >
                <option value="">— Seçin —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id} disabled={a.id === form.to_id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {fromAcc && (
                <div className="text-[11px] text-neutral-500 mt-1 font-mono-num">
                  Bakiye: {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(fromBalance)}
                </div>
              )}
            </Field>
            <div className="hidden md:flex items-center justify-center pb-6">
              <ArrowLeftRight size={22} className="text-neutral-400" />
            </div>
            <Field label="Hedef Hesap">
              <select
                required data-testid="transfer-to"
                value={form.to_id}
                onChange={(e) => setForm({ ...form, to_id: e.target.value })}
                className="input bg-white"
              >
                <option value="">— Seçin —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id} disabled={a.id === form.from_id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {toAcc && (
                <div className="text-[11px] text-neutral-500 mt-1 font-mono-num">
                  Bakiye: {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(balances[form.to_id] ?? toAcc.opening_balance ?? 0)}
                </div>
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tutar (₺)">
              <input
                required type="number" step="0.01" min="0.01" data-testid="transfer-amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="input font-mono-num text-lg"
                placeholder="0,00"
              />
            </Field>
            <Field label="Tarih">
              <input
                required type="date" data-testid="transfer-date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="input"
              />
            </Field>
          </div>
          {willOverdraw && (
            <div className="text-[11px] text-rose-700 border border-rose-200 bg-rose-50 px-3 py-2">
              ⚠ Kaynak hesabın bakiyesi bu virman için yetersiz olabilir. Yine de kaydedilebilir.
            </div>
          )}
          <Field label="Açıklama (opsiyonel)">
            <input
              data-testid="transfer-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="input"
              placeholder={`Ör: ${fromAcc?.name || "Hesap"} → ${toAcc?.name || "Hesap"}`}
            />
          </Field>
          <div className="text-[11px] text-neutral-500 border-l-2 border-black pl-3">
            Bu işlem her iki hesabın bakiyesini otomatik günceller. Gelir/Gider sayfalarında görünmez, sadece bakiye hareketi olarak kayıt tutar.
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="transfer-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Virmani Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}
